// src/components/TuPlan2Mobile.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { ReactComponent as PetfamLogo } from "../assets/PetfamLogo.svg"; // ✅ SVG

import "../styles/interfaz.css";
import "../styles/tuplan2.mobile.css";

import imgMensual from "../assets/mensual.webp";

import { Pencil, X, Lock, PlayCircle } from "lucide-react";
import config from "../config";
import { authFetch } from "../utils/authFetch";

const API_BASE_URL = config.apiBaseUrl;

const PLAN_ID = "mensual";

const PLAN_CONFIG = {
  mensual: {
    id: "mensual",
    label: "Plan Petfam",
    badge: null,
    ahorro: "Incluye hasta 3 mascotas sin costo adicional",
    precio: "$9.000",
    periodo: "/mes",
    descripcionCorta: "Todo lo que tu mascota necesita, en un solo plan mensual.",
    siguientePago: "Cobro automático cada mes.",
    bullets: [
      "Atención veterinaria dentro de la red Petfam",
      "Recordatorios automáticos de vacunas y desparasitación",
      "Carnet digital de tu mascota, siempre a la mano",
    ],
  },
};

const PLAN_IMAGES = { mensual: imgMensual };

const readUserFromStorage = () => {
  try {
    const raw = localStorage.getItem("petfamUser");
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const resolveUserId = (u) => {
  const id = u?.idUser ?? u?.id ?? u?.userId ?? null;
  return id != null ? Number(id) : null;
};

export default function TuPlan2Mobile() {
  const navigate = useNavigate();
  const location = useLocation();

  // ====== Menú usuario ======
  const [openMenu, setOpenMenu] = useState(false);
  const [openVideoGuide, setOpenVideoGuide] = useState(false);
  const [isMobileVideo, setIsMobileVideo] = useState(false);
  const menuRef = useRef(null);
  const toggleMenu = () => setOpenMenu((v) => !v);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && setOpenMenu(false);
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, []);

  const isHomeActive =
    location.pathname === "/interfaz" || location.pathname === "/";
  const isTuPlanActive = location.pathname === "/tu-plan";

  const planId = PLAN_ID;
  const plan = useMemo(() => PLAN_CONFIG[planId], [planId]);

  useEffect(() => {
    localStorage.setItem("petfam_plan", PLAN_ID);
  }, []);

  useEffect(() => {
  const checkDevice = () => {
    setIsMobileVideo(window.innerWidth <= 768);
  };

  checkDevice();
  window.addEventListener("resize", checkDevice);

  return () => window.removeEventListener("resize", checkDevice);
}, []);

  // ==========================
  // ✅ PERFIL (igual a Interfaz)
  // ==========================
  const [openProfilePanel, setOpenProfilePanel] = useState(false);

  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profileName, setProfileName] = useState("");
  const [profilePhone, setProfilePhone] = useState("");
  const [profileLocation, setProfileLocation] = useState("");

  const [editedName, setEditedName] = useState("");
  const [editedPhone, setEditedPhone] = useState("");
  const [editedLocation, setEditedLocation] = useState("");

  const [savingProfile, setSavingProfile] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState("");

  const profileFileRef = useRef(null);
  const [userId, setUserId] = useState(null);

  // ===== CAMBIO DE CONTRASEÑA
  const [openPasswordModal, setOpenPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);

  const resetPasswordForm = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setPasswordError("");
    setPasswordSuccess("");
  };

  const syncUserFromStorage = async () => {
    const local = readUserFromStorage();
    if (!local) return;

    const id = resolveUserId(local);
    if (id) setUserId(id);

    const nm = local?.name ?? "";
    const ph = local?.phone ?? "";
    const ct = local?.country ?? local?.location ?? "";

    setProfileName(nm);
    setEditedName(nm);
    setProfilePhone(ph);
    setEditedPhone(ph);
    setProfileLocation(ct);
    setEditedLocation(ct);

    if (local?.profilePhotoUrl) {
      const url = local.profilePhotoUrl.startsWith("http")
        ? local.profilePhotoUrl
        : `${API_BASE_URL}${local.profilePhotoUrl}`;
      setProfilePhoto(url);
    } else {
      setProfilePhoto(null);
    }

    try {
      if (!id) return;
      const res = await authFetch(`/user/${id}`, { method: "GET" });
      const json = await res.json().catch(() => null);

      if (res.ok && json?.data) {
        const merged = {
          ...local,
          ...json.data,
          email: local.email ?? json.data.email,
          roles: local.roles ?? [],
        };
        localStorage.setItem("petfamUser", JSON.stringify(merged));

        const nm2 = merged?.name ?? "";
        const ph2 = merged?.phone ?? "";
        const ct2 = merged?.country ?? merged?.location ?? "";

        setProfileName(nm2);
        setEditedName(nm2);
        setProfilePhone(ph2);
        setEditedPhone(ph2);
        setProfileLocation(ct2);
        setEditedLocation(ct2);

        if (merged?.profilePhotoUrl) {
          const url2 = merged.profilePhotoUrl.startsWith("http")
            ? merged.profilePhotoUrl
            : `${API_BASE_URL}${merged.profilePhotoUrl}`;
          setProfilePhoto(url2);
        }
      }
    } catch {}
  };

  useEffect(() => {
    syncUserFromStorage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleProfilePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);
    setProfilePhoto(previewUrl);

    const user = readUserFromStorage();
    const id = resolveUserId(user);
    if (!id) return;

    const formData = new FormData();
    formData.append("photo", file);

    try {
      const res = await fetch(`${API_BASE_URL}/user/${id}/photo`, {
        method: "POST",
        body: formData,
      });

      const json = await res.json().catch(() => null);
      if (!json?.data?.profilePhotoUrl) return;

      const serverUrl = json.data.profilePhotoUrl.startsWith("http")
        ? json.data.profilePhotoUrl
        : `${API_BASE_URL}${json.data.profilePhotoUrl}`;

      setProfilePhoto(serverUrl);

      const updatedUser = {
        ...user,
        ...(json.data || {}),
        idUser: json.data.idUser || id,
        profilePhotoUrl: json.data.profilePhotoUrl,
      };
      localStorage.setItem("petfamUser", JSON.stringify(updatedUser));
    } catch {}
  };

  useEffect(() => {
    return () => {
      if (profilePhoto && profilePhoto.startsWith("blob:")) {
        try {
          URL.revokeObjectURL(profilePhoto);
        } catch {}
      }
    };
  }, [profilePhoto]);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError("Todos los campos son obligatorios.");
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError("La nueva contraseña debe tener al menos 6 caracteres.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("La confirmación no coincide con la nueva contraseña.");
      return;
    }
    if (!userId) {
      setPasswordError("No se pudo identificar al usuario.");
      return;
    }

    try {
      setPasswordLoading(true);

      const res = await fetch(`${API_BASE_URL}/auth/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          idUser: userId,
          currentPassword,
          newPassword,
        }),
      });

      const json = await res.json().catch(() => null);

      if (!res.ok || !json || json.status !== 200) {
        setPasswordError(
          json?.userMessage ||
            "No se pudo cambiar la contraseña. Verifica tu contraseña actual."
        );
        return;
      }

      setPasswordSuccess("Tu contraseña se actualizó correctamente.");
      resetPasswordForm();

      setTimeout(() => {
        setOpenPasswordModal(false);
        setPasswordSuccess("");
      }, 1200);
    } catch {
      setPasswordError(
        "Ocurrió un error al conectar con el servidor. Inténtalo de nuevo."
      );
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <>
      <div className="tp2M-shell">
        <header className="header">
          <div className="header-container">
            <div className="nav-left">
              <PetfamLogo className="logo" aria-label="Logo Petfam" />
            </div>

            <nav className="nav-center">
              <Link
                to="/interfaz"
                state={{ noAnim: true }}
                className={`${isHomeActive ? "active" : ""} underline-anim`}
              >
                Inicio
              </Link>
              <Link
                to="/tu-plan"
                className={`${isTuPlanActive ? "active" : ""} underline-anim`}
              >
                Tu plan
              </Link>
            </nav>

            <div className="nav-right">
              <div className="user-menu" ref={menuRef}>
                <button
                  className="icon-user"
                  onClick={toggleMenu}
                  aria-label="Perfil"
                  aria-haspopup="menu"
                  aria-expanded={openMenu}
                  type="button"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="user-svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
                    />
                  </svg>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="arrow-svg"
                  >
                    <path
                      fillRule="evenodd"
                      d="M12.53 16.28a.75.75 0 0 1-1.06 0l-7.5-7.5a.75.75 0 0 1 1.06-1.06L12 14.69l6.97-6.97a.75.75 0 1 1 1.06 1.06l-7.5 7.5Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>

                {openMenu && (
                  <div className="dropdown-menu" role="menu">
                    {/* ✅ IGUAL A INTERFAZ */}
                    <Link
                      to={location.pathname}
                      role="menuitem"
                      onClick={(e) => {
                        e.preventDefault();
                        setOpenMenu(false);
                        setOpenProfilePanel(true);
                        syncUserFromStorage();
                      }}
                    >
                      <Pencil
                        size={18}
                        style={{ marginRight: 10 }}
                        strokeWidth={1.8}
                      />
                      Editar perfil
                    </Link>

                    <button
    type="button"
    role="menuitem"
    className="dropdown-menu-btn"
    onClick={() => {
      setOpenMenu(false);
      setOpenVideoGuide(true);
    }}
  >
    <PlayCircle size={18} style={{ marginRight: 10 }} strokeWidth={1.8} />
    Video guiado
  </button>

                    <hr />

                    <Link
                      to="/"
                      state={{ from: "tuplan2" }}
                      role="menuitem"
                      onClick={() => {
                        localStorage.removeItem("petfamUser");
                        localStorage.removeItem("idUser");
                        localStorage.removeItem("token");
                        localStorage.removeItem("petfam_customer_email");
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        width="20"
                        height="20"
                        style={{ marginRight: 10 }}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6A2.25 2.25 0 0 0 5.25 5.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 9l-3 3m0 0l3 3m-3-3h12.75"
                        />
                      </svg>
                      Salir
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        <main className="tp2M-main" role="main">
          <section className="tp2M-card">
            <h1 className="tp2M-title">{plan.label}</h1>

            {plan.badge && <span className="tp2M-badge">{plan.badge}</span>}

            <figure className="tp2M-figure">
              <img src={PLAN_IMAGES[planId]} alt={`Imagen del ${plan.label}`} />
            </figure>

            <p className="tp2M-description">{plan.descripcionCorta}</p>

            <div className="tp2M-price-row">
              <span className="tp2M-price">{plan.precio}</span>
              <span className="tp2M-periodo">{plan.periodo}</span>
            </div>

            <p className="tp2M-ahorro">{plan.ahorro}</p>
            <p className="tp2M-renovacion">{plan.siguientePago}</p>

            <ul className="tp2M-benefits">
              {plan.bullets.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>

            <p className="tp2M-small-note">
  Cancela cuando quieras escribiéndonos a{" "}
  <a
    href={`mailto:soporte@petfam.club?subject=${encodeURIComponent(
      "SOLICITUD DE CANCELACIÓN"
    )}`}
  >
    soporte@petfam.club
  </a>
</p>
          </section>
        </main>
      </div>

      {/* ✅ PANEL EDITAR PERFIL */}
      {openProfilePanel && (
        <div
          className="profile-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpenProfilePanel(false);
          }}
        >
          <div className="profile-panel" onClick={(e) => e.stopPropagation()}>
            <button
              className="profile-close"
              onClick={() => setOpenProfilePanel(false)}
              aria-label="Cerrar edición de perfil"
            >
              <X size={18} />
            </button>

            <h2 className="profile-title">Edita tu perfil</h2>

            <div className="profile-top">
              <div className="profile-photo-edit">
                <input
                  ref={profileFileRef}
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePhotoChange}
                  className="sr-only-input"
                />

                <button
                  type="button"
                  className={`profile-photo-box ${profilePhoto ? "has-photo" : ""}`}
                  onClick={() => profileFileRef.current?.click()}
                >
                  {profilePhoto ? (
                    <img src={profilePhoto} alt="Foto de perfil" />
                  ) : (
                    <>
                      <span className="profile-photo-plus">+</span>
                      <span className="profile-photo-text">Editar foto</span>
                    </>
                  )}
                </button>
              </div>

              <div className="profile-fields">
                <label className="profile-label">
                  Nombre
                  <input
                    type="text"
                    value={profileName}
                    onChange={(e) => {
                      setProfileName(e.target.value);
                      setEditedName(e.target.value);
                    }}
                  />
                </label>

                <label className="profile-label">
                  Celular
                  <input
                    type="tel"
                    value={profilePhone}
                    onChange={(e) => {
                      setProfilePhone(e.target.value);
                      setEditedPhone(e.target.value);
                    }}
                  />
                </label>

                <label className="profile-label">
                  Ubicación
                  <input
                    type="text"
                    value={profileLocation}
                    onChange={(e) => {
                      const v = e.target.value;
                      setProfileLocation(v);
                      setEditedLocation(v);
                    }}
                  />
                </label>

                <div className="profile-save-box" style={{ marginTop: 10 }}>
                  {saveSuccess && <p className="save-success">✔ Cambios guardados</p>}
                  {saveError && <p className="save-error">⚠ {saveError}</p>}

                  <button
                    type="button"
                    className="profile-save-btn"
                    onClick={async () => {
                      setSavingProfile(true);
                      setSaveError("");
                      setSaveSuccess(false);

                      try {
                        const user = readUserFromStorage();
                        if (!user) throw new Error("No user");
                        const id = resolveUserId(user);
                        if (!id) throw new Error("No idUser");

                        const res = await fetch(`${API_BASE_URL}/user/${id}`, {
                          method: "PUT",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({
                            name: editedName,
                            phone: editedPhone,
                            country: editedLocation,
                          }),
                        });

                        const json = await res.json().catch(() => null);
                        if (!res.ok) throw new Error(json?.userMessage || "Error guardando");

                        const updatedUser = {
                          ...user,
                          ...(json?.data || {}),
                          name: editedName,
                          phone: editedPhone,
                          country: editedLocation,
                          location: editedLocation,
                        };

                        localStorage.setItem("petfamUser", JSON.stringify(updatedUser));

                        setProfileName(editedName);
                        setProfilePhone(editedPhone);
                        setProfileLocation(editedLocation);

                        setSaveSuccess(true);
                        setTimeout(() => setSaveSuccess(false), 1500);
                      } catch {
                        setSaveError("No se pudieron guardar los cambios");
                      } finally {
                        setSavingProfile(false);
                      }
                    }}
                    disabled={savingProfile}
                  >
                    {savingProfile ? "Guardando..." : "Guardar cambios"}
                  </button>
                </div>
              </div>
            </div>

            <div className="profile-bottom">
              <button
                type="button"
                className="profile-link"
                onClick={() => {
                  resetPasswordForm();
                  setOpenProfilePanel(false);
                  setOpenPasswordModal(true);
                }}
              >
                <Lock size={16} />
                <span>Editar contraseña</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ MODAL CAMBIAR CONTRASEÑA */}
      {openPasswordModal && (
        <div className="password-modal-overlay">
          <div className="password-modal pro">
            <button
              type="button"
              className="password-close"
              onClick={() => {
                resetPasswordForm();
                setOpenPasswordModal(false);
              }}
              aria-label="Cerrar"
            >
              <X size={18} />
            </button>

            <div className="password-header">
              <h3>Cambiar contraseña</h3>
              <p>
                Primero valida tu contraseña actual.
                <br />
                Luego podrás definir una nueva.
              </p>
            </div>

            {passwordError && (
              <p className="password-message password-error">{passwordError}</p>
            )}
            {passwordSuccess && (
              <p className="password-message password-success">{passwordSuccess}</p>
            )}

            <form onSubmit={handlePasswordSubmit} className="password-form">
              <label>
                Contraseña actual
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="••••••••"
                  autoFocus
                />
              </label>

              <label>
                Nueva contraseña
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Mínimo 8 caracteres"
                />
              </label>

              <label>
                Confirmar nueva contraseña
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repite la nueva contraseña"
                />
              </label>

              <div className="password-actions">
                <button
                  type="button"
                  className="password-cancel"
                  onClick={() => {
                    resetPasswordForm();
                    setOpenPasswordModal(false);
                  }}
                  disabled={passwordLoading}
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  className="password-submit"
                  disabled={passwordLoading}
                >
                  {passwordLoading ? "Guardando..." : "Guardar contraseña"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {openVideoGuide && (
  <div
    className="video-guide-overlay"
    onClick={(e) => {
      if (e.target === e.currentTarget) setOpenVideoGuide(false);
    }}
  >
    <div className="video-guide-modal" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="video-guide-close"
        onClick={() => setOpenVideoGuide(false)}
        aria-label="Cerrar video guiado"
      >
        <X size={18} />
      </button>

      <h3 className="video-guide-title">Video guiado</h3>

      <p className="video-guide-subtitle">
        {isMobileVideo
          ? "Conoce cómo moverte en la plataforma."
          : "Conoce cómo moverte en la plataforma."}
      </p>

      <div className="video-guide-box">
        <video
          key={isMobileVideo ? "cel" : "pc"}
          controls
          autoPlay
          playsInline
          preload="metadata"
          className="video-guide-player"
        >
          <source
            src={isMobileVideo ? "/videos/videocel.mp4" : "/videos/videopc.mp4"}
            type="video/mp4"
          />
          Tu navegador no soporta video.
        </video>
      </div>
    </div>
  </div>
)}
    </>
  );
}
